<template>
  <o-dialog
    ref="dialog"
    :title="$t('updatePrices.title')"
    :info="hasEmployeeServicePriceChanged ? $t('components.service.actions.updatePrices.priceChangedInfo') : $t('components.service.actions.updatePrices.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel', callback: cancel },
      { text: $t('general.confirm'), type: 'confirm', callback: confirm }
    ]"
    :loading="loading"
    @close="handleDialogClose"
  >
    <div class="-mx-4 border-t border-b p-4 text-left">
      <o-field-group
        v-if="!hasEmployeeServicePriceChanged"
        :label="$t('components.service.actions.updatePrices.updateAllEmployees')"
        :info="$t('components.service.actions.updatePrices.updateAllEmployeesInfo')"
        class="block mb-6"
        col
        reverse
      >
        <toggle-field v-model="updateEmployees" />
      </o-field-group>

      <o-field-group
        :label="$t('updatePrices.updateFutureAppointments')"
        :info="$t('updatePrices.updateFutureAppointmentsInfo')"
        class="mb-3"
        col
        reverse
      >
        <toggle-field
          v-model="updateFutureReservations"
        />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { OFieldGroup } from '@/components/elements'
import { ToggleField } from '@/components/fields'

import type { Service } from '~/_api/models/service'

const props = defineProps<{
  hasEmployeeServicePriceChanged?: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:loading', loading: boolean): void
}>()

const { $api, $notification, $bus, $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

// Template refs
const dialog = ref()

// State
const loading = ref(false)
const service = ref<Service | null>(null)
const updateEmployees = ref(false)
const updateFutureReservations = ref(false)
const resolvePromise = ref<((value: boolean) => void) | null>(null)

const dialogActive = computed(() => {
  return dialog.value?.active ?? false
})

watch(dialogActive, (newVal) => {
  console.log('dialog active status changed:', newVal)
})

const handleDialogClose = () => {
  resolvePromise.value?.(true)
}

const open = (serviceInstance: Service) => {
  service.value = serviceInstance
  dialog.value?.open()

  return new Promise<boolean>((resolve) => {
    resolvePromise.value = resolve
  })
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    if (updateEmployees.value || updateFutureReservations.value) {
      const response = await $api.service.updatePrices(service.value!, {
        updateEmployees: updateEmployees.value,
        updateFutureReservations: updateFutureReservations.value,
      })

      if (!response.success) {
        $notification.error({ errors: response.errors })
        resolvePromise.value?.(false)
        return false
      }

      emit('success')
      $bus.emit.updated('Service', service.value)
    }

    resolvePromise.value?.(true)

    return true
  } catch (e: any) {
    $notification.error({ errors: e?.errors })

    emit('error')

    resolvePromise.value?.(true)

    return false
  } finally {
    emit('update:loading', false)
  }
}

const cancel = () => {
  resolvePromise.value?.(true)
  dialog.value?.close()
}

defineExpose({
  open,
})
</script>
